import { IconButton, ListItemIcon, ListItemText, MenuItem, Stack, Tooltip } from '@mui/material';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { CalculatorVariantIcon, DeleteIcon, EditButtonIcon } from '@shared/custom-icons';
import { Link } from 'react-router';
import {
  AccountTreeOutlined,
  Add,
  AspectRatio,
  MoreVert,
  PriceChangeOutlined,
  ReadMore,
  SwapHoriz,
} from '@mui/icons-material';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { CalculusTab } from '@retail/calculus/types';
import { FILTER_CONFIG } from '@retail/products/utils';
import { Menu, MenuButton } from '@retail/components';
interface Props {
  supplierIdForCalculus?: number;
  addItemToAssortment?: () => void;
  editItem?: () => void;
  editPrices?: () => void;
  editTags?: () => void;
  deleteItem?: () => void;
  replaceItem?: () => void;
  isAlreadyReplaced?: boolean;
  viewItemCalculusCard?: () => void;
  viewItemProductCard?: () => void;
}

export const AssortmentTableActions = ({
  supplierIdForCalculus,
  addItemToAssortment,
  editItem,
  editPrices,
  editTags,
  deleteItem,
  replaceItem,
  isAlreadyReplaced,
  viewItemCalculusCard,
  viewItemProductCard,
}: Props) => {
  const t = useMyAssortmentTFunction();

  const hasItemActions =
    !!supplierIdForCalculus ||
    !!addItemToAssortment ||
    !!editItem ||
    !!editPrices ||
    !!editTags ||
    !!replaceItem ||
    !!deleteItem;

  return (
    <Stack flexDirection="row" alignItems="center" gap={1.5}>
      {hasItemActions && (
        <Menu onKeyDown={(event) => event.stopPropagation()}>
          {({ onClose }) => (
            <>
              <Tooltip title={t('myAssortment.actions.itemActions')}>
                <MenuButton component={IconButton} sx={{ p: 0 }}>
                  <MoreVert fontSize="small" />
                </MenuButton>
              </Tooltip>
              {supplierIdForCalculus && (
                <MenuItem
                  component={Link}
                  to={{
                    pathname: mgPrisRoutes.calculus.calculusArea.getFullLinkPath({
                      calculusArea: CalculusTab.ITEMS,
                    }),
                    search: `${FILTER_CONFIG.supplierNumber.urlKey}=${supplierIdForCalculus}`,
                  }}
                  onClick={onClose}
                >
                  <ListItemIcon>
                    <CalculatorVariantIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.goToCalculation')}</ListItemText>
                </MenuItem>
              )}
              {addItemToAssortment && (
                <MenuItem
                  onClick={() => {
                    addItemToAssortment();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <Add fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.addToOwnAssortment')}</ListItemText>
                </MenuItem>
              )}
              {editItem && (
                <MenuItem
                  onClick={() => {
                    editItem();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <EditButtonIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.editItem')}</ListItemText>
                </MenuItem>
              )}
              {editPrices && (
                <MenuItem
                  onClick={() => {
                    editPrices();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <PriceChangeOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.editSalesPrice')}</ListItemText>
                </MenuItem>
              )}
              {editTags && (
                <MenuItem
                  onClick={() => {
                    editTags();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <AccountTreeOutlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.editTags')}</ListItemText>
                </MenuItem>
              )}
              {replaceItem && (
                <MenuItem
                  disabled={isAlreadyReplaced}
                  onClick={() => {
                    replaceItem();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <SwapHoriz fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>
                    {isAlreadyReplaced
                      ? t('myAssortment.actions.itemAlreadyReplaced')
                      : t('myAssortment.actions.replaceItem')}
                  </ListItemText>
                </MenuItem>
              )}
              {deleteItem && (
                <MenuItem
                  onClick={() => {
                    deleteItem();
                    onClose();
                  }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{t('myAssortment.actions.deleteItem')}</ListItemText>
                </MenuItem>
              )}
            </>
          )}
        </Menu>
      )}
      {viewItemProductCard && (
        <Tooltip title={t('myAssortment.actions.seeProductInfo')}>
          <IconButton onClick={viewItemProductCard} sx={{ p: 0 }}>
            <AspectRatio fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {viewItemCalculusCard && (
        <Tooltip title={t('myAssortment.actions.seeCalculusCard')}>
          <IconButton onClick={viewItemCalculusCard} sx={{ p: 0 }}>
            <ReadMore />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};
